import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useContext,
} from "react";
import axios from "axios";
import "../../../../ComponentsCss/GenerateSalaryComponent/PayRole/RunPayroll/RunPayroll.css";
import { usePopup } from "../../../../../context/popup-context/Popup";
import { Popup } from "../../../../Utils/Popup/Popup";
import { GlobalContext } from "../../../../../context/GlobalContext/GlobalContext";

export default function RunPayroll() {
  const [loading, setLoading] = useState(false);
  const [payrollData, setPayrollData] = useState([]);

  // Breakdown Modal State
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showBreakdown, setShowBreakdown] = useState(false);

  const { showPopup, setShowPopup, setMessage } = usePopup();

  // 🚨 Grab EVERYTHING from the Global Context!
  const { globalData } = useContext(GlobalContext);
  const companyCode = globalData?.userInfo?.companyCode;
  const month = globalData?.month;
  const year = globalData?.year;
  const { periodKey } = globalData?.payCycle || {};

  // Helper to get cookie for Auth Token
  const getCookieValue = (name) =>
    document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${name}=`))
      ?.split("=")[1];

  const token = useMemo(() => getCookieValue("authToken"), []);

  // ---------------------------------------------------------
  // 1. DATA FETCHING (ONLY FIRES ON BUTTON CLICK NOW)
  // ---------------------------------------------------------
  const fetchPayrollData = useCallback(async () => {
    // Prevent running if no company or period is selected yet
    if (!companyCode || !periodKey) {
      setShowPopup(true);
      setMessage(
        "Please ensure Company and Payroll Period are selected first.",
      );
      setTimeout(() => setShowPopup(false), 3000);
      return;
    }

    setLoading(true);

    try {
      // API 1: Attendance & Payable Days (Uses YYYY-MM format)
      const formattedMonth = `${year}-${month.toString().padStart(2, "0")}`;
      const attendanceReq = axios.get(
        `${process.env.REACT_APP_API_URL}/payrole/get_all_payroleCounts_monthwise`,
        {
          params: { companyCode, month: formattedMonth, limit: 1000 },
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // API 2: Salary Structure (CTC)
      const ctcReq = axios.get(
        `${process.env.REACT_APP_API_URL}/ctc-structure/get_all_employee_salaries`,
        {
          params: { companyCode, periodKey },
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // API 3: Adjustments (🚨 NOW USING periodKey from context)
      const adjustmentReq = axios.get(
        `${process.env.REACT_APP_API_URL}/pay_adjustment/get_adjustments`,
        {
          params: { companyCode, periodKey },
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // Execute in Parallel
      const [attendanceRes, ctcRes, adjustmentRes] = await Promise.all([
        attendanceReq,
        ctcReq,
        adjustmentReq,
      ]);

      const attendanceList = attendanceRes.data.data || [];
      const ctcList = ctcRes.data.data || [];

      // Flatten Adjustments
      const adjustmentsMap = {};
      if (adjustmentRes.data.success) {
        adjustmentRes.data.data.forEach((doc) => {
          adjustmentsMap[doc.employee_id] = doc.adjustments || [];
        });
      }

      // Merge and Calculate
      // const processed = attendanceList.map((attRecord) => {
      //   const empId = attRecord.employee_id;
      //   const totalDays = attRecord.totalDays || 30; // Avoid division by zero
      //   const payableDays = attRecord.payableDays || 0;
      //   const totalLOP = attRecord.totalLOP || 0;

      //   // Find CTC
      //   const ctcRecord = ctcList.find((c) => c.employee_id === empId);
      //   const salaryComponents = ctcRecord?.componentBreakup || [];

      //   // Find Adjustments
      //   const empAdjustments = adjustmentsMap[empId] || [];

      //   let dbGrossSalary = 0; // To hold the exact "Gross Salary" component value
      //   let fallbackEarnings = 0; // Just in case "Gross Salary" is missing
      //   let totalDeductions = 0;
      //   let adjustmentEarnings = 0;

      //   const processedComponents = [];

      //   // A. Calculate Prorated Salary Components
      //   salaryComponents.forEach((comp) => {
      //     const monthlyAmount = parseFloat(comp.amount) || 0;

      //     // Pro-rata Formula: (Monthly Amount / Total Days) * Payable Days
      //     const perDay = monthlyAmount / totalDays;
      //     const calculatedAmount = perDay * payableDays;

      //     const payType = comp.pay_type?.toLowerCase() || "";
      //     const compName = comp.component?.toLowerCase() || "";

      //     // 1. Look for the explicit "Gross Salary" component
      //     if (compName.includes("gross")) {
      //       dbGrossSalary = calculatedAmount;
      //     }
      //     // 2. Sum up ALL Deductions statically
      //     else if (payType === "deduction") {
      //       totalDeductions += calculatedAmount;
      //     }
      //     // (Fallback: Track other earnings just in case DB Gross is missing)
      //     else if (payType === "earning") {
      //       fallbackEarnings += calculatedAmount;
      //     }
      //     processedComponents.push({
      //       name: comp.component,
      //       type: comp.pay_type,
      //       monthlyAmount: monthlyAmount,
      //       calculatedAmount: calculatedAmount,
      //       isAdjustment: false,
      //     });
      //   });

      //   // B. Add Adjustments
      //   empAdjustments.forEach((adj) => {
      //     const amount = parseFloat(adj.amount) || 0;
      //     const isEarning =
      //       adj.type?.toLowerCase() === "earnings" ||
      //       adj.type?.toLowerCase() === "earning";

      //     if (isEarning) {
      //       adjustmentEarnings += amount;
      //     } else if (adj.type?.toLowerCase() === "deduction") {
      //       totalDeductions += amount;
      //     }

      //     processedComponents.push({
      //       name: adj.component_name || adj.componentName || "Adjustment",
      //       type: adj.type || (isEarning ? "Earning" : "Deduction"),
      //       monthlyAmount: amount,
      //       calculatedAmount: amount,
      //       isAdjustment: true,
      //     });
      //   });

      //   // C. Final Static Math
      //   const finalGrossEarnings =
      //     (dbGrossSalary > 0 ? dbGrossSalary : fallbackEarnings) +
      //     adjustmentEarnings;
      //   const netPay = finalGrossEarnings - totalDeductions;

      //   return {
      //     employee_id: empId,
      //     name: attRecord.employee_name,
      //     totalLOP,
      //     totalDays,
      //     payableDays,
      //     grossEarnings: finalGrossEarnings,
      //     grossDeductions: totalDeductions,
      //     netPay: netPay,
      //     details: processedComponents,
      //   };
      // });
      // Merge and Calculate
      const ctcByEmployee = {};
      ctcList.forEach((c) => {
        if (!ctcByEmployee[c.employee_id]) ctcByEmployee[c.employee_id] = [];
        ctcByEmployee[c.employee_id].push(c);
      });
      Object.values(ctcByEmployee).forEach((arr) =>
        arr.sort(
          (a, b) => new Date(a.effective_from) - new Date(b.effective_from),
        ),
      );
      const fmtDate = (d) =>
        d
          ? new Date(d).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
            })
          : "";

      const processed = attendanceList.map((attRecord) => {
        const empId = attRecord.employee_id;
        const empAdjustments = adjustmentsMap[empId] || [];
        const empCTCs = ctcByEmployee[empId] || [];

        // Fall back to a single whole-period "segment" for employees with no
        // segment data yet (e.g. records frozen before this change shipped)
        const attSegments =
          attRecord.segments && attRecord.segments.length
            ? attRecord.segments
            : [
                {
                  ctc_structure_id: null,
                  totalDays: attRecord.totalDays || 30,
                  payableDays: attRecord.payableDays || 0,
                },
              ];

        let dbGrossSalary = 0;
        let fallbackEarnings = 0;
        let totalDeductions = 0;
        let adjustmentEarnings = 0;
        const processedComponents = [];

        attSegments.forEach((seg) => {
          const ctcRecord = seg.ctc_structure_id
            ? empCTCs.find(
                (c) => String(c._id) === String(seg.ctc_structure_id),
              )
            : empCTCs[empCTCs.length - 1];

          const salaryComponents = ctcRecord?.componentBreakup || [];
          const periodTotalDays = attRecord.totalDays || 30; // ✅ always the whole period, not seg.totalDays
          const segPayableDays = seg.payableDays || 0;

          const segLabel =
            seg.effective_from && seg.effective_to
              ? `${fmtDate(seg.effective_from)} - ${fmtDate(seg.effective_to)}`
              : null;

          salaryComponents.forEach((comp) => {
            const monthlyAmount = parseFloat(comp.amount) || 0;
            const perDay = monthlyAmount / periodTotalDays; // ✅ fixed denominator
            const calculatedAmount = perDay * segPayableDays;
            // ...rest unchanged

            const payType = comp.pay_type?.toLowerCase() || "";
            const compName = comp.component?.toLowerCase() || "";

            if (compName.includes("gross")) {
              dbGrossSalary += calculatedAmount;
            } else if (payType === "deduction") {
              totalDeductions += calculatedAmount;
            } else if (payType === "earning") {
              fallbackEarnings += calculatedAmount;
            }

            processedComponents.push({
              name: comp.component,
              type: comp.pay_type,
              monthlyAmount,
              calculatedAmount,
              isAdjustment: false,
              segmentLabel: segLabel, // ✅ NEW — lets the breakdown modal show which CTC period this line came from
            });
          });
        });

        // Adjustments stay flat / period-level — NOT split across CTC segments
        empAdjustments.forEach((adj) => {
          const amount = parseFloat(adj.amount) || 0;
          const isEarning =
            adj.type?.toLowerCase() === "earnings" ||
            adj.type?.toLowerCase() === "earning";

          if (isEarning) {
            adjustmentEarnings += amount;
          } else if (adj.type?.toLowerCase() === "deduction") {
            totalDeductions += amount;
          }

          processedComponents.push({
            name: adj.component_name || adj.componentName || "Adjustment",
            type: adj.type || (isEarning ? "Earning" : "Deduction"),
            monthlyAmount: amount,
            calculatedAmount: amount,
            isAdjustment: true,
          });
        });

        const finalGrossEarnings =
          (dbGrossSalary > 0 ? dbGrossSalary : fallbackEarnings) +
          adjustmentEarnings;
        const netPay = finalGrossEarnings - totalDeductions;

        return {
          employee_id: empId,
          name: attRecord.employee_name,
          totalLOP: attRecord.totalLOP,
          totalDays: attRecord.totalDays,
          payableDays: attRecord.payableDays,
          grossEarnings: finalGrossEarnings,
          grossDeductions: totalDeductions,
          netPay: netPay,
          details: processedComponents,
        };
      });
      setPayrollData(processed);
    } catch (error) {
      console.error("Payroll Calculation Error:", error);
      setShowPopup(true);
      setMessage("Failed to fetch payroll data. Check console.");
      setTimeout(() => setShowPopup(false), 3000);
    } finally {
      setLoading(false);
    }
  }, [companyCode, month, year, periodKey, token, setShowPopup, setMessage]);

  // 🚨 SECURITY FIX: If the user changes the month/year in the top UI,
  // clear the table so they are FORCED to click "Process Payroll" again!
  useEffect(() => {
    setPayrollData([]);
  }, [companyCode, periodKey]);

  // ---------------------------------------------------------
  // 3. UI HANDLERS
  // ---------------------------------------------------------
  const handleViewBreakdown = (employee) => {
    setSelectedEmployee(employee);
    setShowBreakdown(true);
  };

  const closeBreakdown = () => {
    setShowBreakdown(false);
    setSelectedEmployee(null);
  };

  const handleSavePayroll = async () => {
    if (payrollData.length === 0) {
      setShowPopup(true);
      setMessage("No payroll data to save. Please process payroll first.");
      setTimeout(() => setShowPopup(false), 3000);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        companyCode,
        month,
        year,
        periodKey, // Included so backend DB indexes perfectly
        payrollData,
      };

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/payslip/save_processed_payroll`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (res.data.success) {
        setShowPopup(true);
        setMessage(
          `✅ Payroll Saved! Inserted: ${res.data.inserted}, Updated: ${res.data.updated}`,
        );
        setTimeout(() => setShowPopup(false), 4000);
      }
    } catch (error) {
      console.error("Save Payroll Error:", error);
      setShowPopup(true);
      // setMessage(error.message);
      const backendMessage =
        error.response?.data?.message || "❌ Failed to save payroll.";

      setMessage(backendMessage);
      setTimeout(() => setShowPopup(false), 8000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="run-payroll-container">
      {/* HEADER & FILTERS */}
      <div className="payroll-header">
        <div className="payroll-filters">
          <button
            className="primary-btn-payroll"
            onClick={fetchPayrollData} // 🚨 User MUST click this to fetch data
            disabled={loading}
          >
            {loading ? "Processing..." : "Process Payroll"}
          </button>
          <button
            className="success-btn-payroll"
            onClick={handleSavePayroll}
            disabled={loading || payrollData.length === 0}
          >
            Save Payroll
          </button>
        </div>
      </div>

      {/* SUMMARY TABLE */}
      <div className="table-container__pay_adjustment">
        <table className="attendance-table__pay_adjustment">
          <thead>
            <tr>
              <th>Emp ID</th>
              <th>Name</th>
              <th>Days (Payable/Total)</th>
              <th className="text-right">Gross Earnings</th>
              <th className="text-right">Gross Deductions</th>
              <th className="text-right">Net Pay</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="text-center">
                  Calculating Salaries...
                </td>
              </tr>
            ) : payrollData.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center">
                  No Data Processed. Click "Process Payroll".
                </td>
              </tr>
            ) : (
              payrollData.map((emp) => (
                <tr key={emp.employee_id}>
                  <td>{emp.employee_id}</td>
                  <td>{emp.name}</td>
                  <td>
                    <span
                      className={`badge ${
                        emp.payableDays < emp.totalDays ? "warning" : "success"
                      }`}
                    >
                      {emp.payableDays} / {emp.totalDays}
                    </span>
                  </td>
                  <td className="text-right">
                    ₹{emp.grossEarnings.toFixed(2)}
                  </td>
                  <td className="text-right text-red">
                    ₹{emp.grossDeductions.toFixed(2)}
                  </td>
                  <td className="text-right font-bold">
                    ₹{emp.netPay.toFixed(2)}
                  </td>
                  <td>
                    <button
                      className="view-btn"
                      onClick={() => handleViewBreakdown(emp)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* BREAKDOWN MODAL */}
      {showBreakdown && selectedEmployee && (
        <div className="popup-overlay">
          <div className="popup-content large-popup">
            <div className="popup-header">
              <h3>Salary Breakdown: {selectedEmployee.name}</h3>
              <button onClick={closeBreakdown} className="close-btn">
                &times;
              </button>
            </div>

            <div className="popup-body-scrollable">
              <div className="summary-cards">
                <div className="card">
                  <span>LOPs</span>
                  <strong>{selectedEmployee.totalLOP}</strong>
                </div>
                <div className="card">
                  <span>Payable Days</span>
                  <strong>{selectedEmployee.payableDays}</strong>
                </div>
                <div className="card">
                  <span>Net Pay</span>
                  <strong>₹{selectedEmployee.netPay.toFixed(2)}</strong>
                </div>
              </div>

              <table className="breakdown-table">
                <thead>
                  <tr>
                    <th>Component</th>
                    <th>Type</th>
                    <th>Monthly Rate</th>
                    <th>Calculated Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedEmployee.details.map((item, idx) => (
                    <tr
                      key={idx}
                      className={item.isAdjustment ? "adjustment-row" : ""}
                    >
                      <td>
                        {item.name}
                        {item.isAdjustment && (
                          <span className="tag-adj">Adjustment</span>
                        )}
                        {item.segmentLabel && (
                          <div
                            className="text-muted"
                            style={{ fontSize: "11px" }}
                          >
                            {item.segmentLabel}
                          </div>
                        )}
                      </td>
                      <td>{item.type}</td>
                      <td>₹{item.monthlyAmount.toLocaleString()}</td>
                      <td className="font-bold">
                        ₹{item.calculatedAmount.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {showPopup && <Popup />}
    </div>
  );
}
